// "Signed in as Paola Hernandez. Not you? Log out" at the bottom of steps 2–4 (decided 2026-10-30).
//
// Log out signs this tab out and goes back to step 1 of the same casting call. Everything kept in
// the tab for that person goes with it (the sign-in, the signup session, their uploads, the send
// and checkout markers, step 4's state); the casting call stays. Step 1 then shows the usual form:
// a classic-site session in the browser is hidden for this tab (classicSession.ts), and the
// classic site itself is never logged out from here.

import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import styled from "styled-components";
import i18next from "i18next";
import { getSubmissionAccountName, rememberSubmissionAccountName } from "../../../helpers/submissionAuth";
import { getSubmissionFlowApi, isMockApi } from "../../../shared/api/submission";
import { resetMockBackend } from "../../../shared/api/submission/SubmissionFlowApi.mock";
import { dismissClassicSession } from "./classicSession";
import { forgetTabAccount } from "./tabAccount";
import { forgetMembershipAsked } from "../../../models/submissionStep4Model";
import { trackSubmissionEvent } from "../analytics";
import { MEMBERSHIP_DARK_BLUE } from "./membershipButton";

interface Props extends DispatchProps {
  history: any;
  search: string;
  step: number;
}

const SignedInAs: React.FC<Props> = ({ history, search, step, resetFlow, resetStep4 }) => {
  const [name, setName] = useState(() => getSubmissionAccountName());

  // Signed up in this flow, or signed in before names were kept: asked once, then kept in the tab.
  useEffect(() => {
    if (name) return;
    let live = true;
    getSubmissionFlowApi()
      .getAccountName()
      .then((found) => {
        if (!found || !live) return;
        rememberSubmissionAccountName(found.firstName, found.lastName);
        setName(found);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLogOut = async () => {
    trackSubmissionEvent("sf_log_out", { step });
    forgetMembershipAsked();
    dismissClassicSession();
    forgetTabAccount();
    await resetFlow();
    resetStep4();
    // The mock keeps its "server" in the tab too: without this, step 1 finds the account
    // already made and refuses a second one.
    if (isMockApi()) resetMockBackend();
    history.replace(`/submission/flow/step/1${search}`);
  };

  const fullName = name ? [name.firstName, name.lastName].filter((part) => !!part).join(" ") : "";

  return (
    <Row>
      {fullName && <span>{i18next.t("Signed in as {{name}}.", { name: fullName })} </span>}
      <span>
        {i18next.t("Not you?")}{" "}
        <LogOut type="button" onClick={onLogOut}>
          {i18next.t("Log out")}
        </LogOut>
      </span>
    </Row>
  );
};

const Row = styled.p`
  margin: 28px 0 8px;
  text-align: center;
  font-size: 13px;
  line-height: 20px;
  color: #53627c;
  overflow-wrap: anywhere;
`;

const LogOut = styled.button`
  padding: 2px;
  background: none;
  border: 0;
  font-size: 13px;
  color: ${MEMBERSHIP_DARK_BLUE};
  text-decoration: underline;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid #0584d7;
    outline-offset: 2px;
  }
`;

interface DispatchProps {
  resetFlow: () => Promise<any>;
  resetStep4: () => void;
}

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  resetFlow: dispatch.submissionSignupModel.resetFlow,
  resetStep4: dispatch.submissionStep4Model.reset,
});

export default connect(null, mapDispatchToProps)(SignedInAs);
